import axios from "axios";
import useSWR from "swr";
import { getRandomString } from "~/assets/js/Common";
import {
    groupBy,
    groupByDay,
} from "~/assets/js/pages/Checkout/sections/TimeSlotSelect/formatDateTime";
import CartSession from "../global/cartSession";
import { subsFromDate } from "../Helpers/date-utils";

const baseUrl = reactConfig.url;
const STORE_IS_OPEN = true;

const getApiQueryString = () => {
    const areaId = window.cookie.get().area
        ? window.cookie.get().area.id
        : false;

    if (!areaId) {
        return false;
    }

    const query = new URLSearchParams();
    query.append("area", areaId);
    query.append("r", getRandomString());
    if (CartSession.isSet()) {
        let cart = CartSession.get();
        if (cart && cart.discount && cart.discount.coupons) {
            let codes = cart.discount.coupons
                .map((coupon) => coupon.code)
                .filter((code) => code.toUpperCase().startsWith("15HELLO"));
            if (codes.length) {
                query.append("discounted", "1");
            }
        }
    }
    const queryString = `?${query.toString()}`;

    return queryString;
};

const fetchDeliveryTimesInformation = async () => {
    const queryString = getApiQueryString();

    if (!queryString) {
        throw new Error("No query params!");
    }

    return await axios.get(`${baseUrl}/time-slots${queryString}`);
};

export const getDeliveryTimes = async () => {
    try {
        const response = await fetchDeliveryTimesInformation();
        if (response.status === 200) {
            return formatDeliveryTimes(response.data.time_slots);
        }
        return [];
    } catch (err) {
        return [];
    }
};

export const getOpeningTimes = async () => {
    try {
        const response = await fetchDeliveryTimesInformation();
        if (response.status === 200) {
            return response.data.opening_times;
        }
        return [];
    } catch (err) {
        return [];
    }
};

export const fetchStoreOpeningTimes = async () => {
    try {
        const response = await fetchDeliveryTimesInformation();
        const { data } = response;
        const storeInformation = {
            deliveryTimes: formatToDeliveryTimes(data.time_slots),
            openingTimes: data.opening_times,
        };
        return { ok: true, storeInformation };
    } catch (err) {
        return { ok: false, storeInformation: {} };
    }
};

export const getHasExpiredSlots = async () => {
    try {
        const response = await fetchDeliveryTimesInformation();
        if (response.status === 200) {
            return response.data.has_expired_slots;
        }
        return false;
    } catch (err) {
        return [];
    }
};

export const fetchIsHoliday = async () => {
    try {
        const response = await fetchDeliveryTimesInformation();
        if (response.status === 200) {
            return response.data.holiday;
        }
        return STORE_IS_OPEN;
    } catch (err) {
        return STORE_IS_OPEN;
    }
};

const formatDeliveryTimes = (deliveryTimes) => {
    const groupByDeliveryDay = groupBy("deliveryDay");
    const groupedDeliveryTimes = groupByDeliveryDay(groupByDay(deliveryTimes));

    return groupedDeliveryTimes;
};

const formatToDeliveryTimes = (deliveryTimes) => {
    return deliveryTimes.map((deliveryTime) => {
        deliveryTime.deliveryDay = deliveryTime.begin.split("T")[0];
        deliveryTime.deliveryHourStart = deliveryTime.begin
            .split("T")[1]
            .substring(0, 5);
        deliveryTime.deliveryHourEnd = deliveryTime.end
            .split("T")[1]
            .substring(0, 5);
        return {
            ...deliveryTime,
            timeSlot: `${deliveryTime.deliveryHourStart} - ${deliveryTime.deliveryHourEnd} Uhr`,
        };
    });
};

export const useDeliveryTimes = ({ buffer } = { buffer: false }) => {
    const { data, error, mutate } = useSWR(
        "/timeslots",
        fetchDeliveryTimesInformation
    );

    const isLoading = !error && !data;
    const isError = error;

    const firstAvailableTimeslot = (buffer) => {
        if (!data) {
            return null;
        }

        return (
            data.data.time_slots.find((slot) => {
                if (buffer) {
                    const slotWithBuffer = subsFromDate({
                        date: slot.valid_till,
                        ...buffer,
                    });

                    return slot.free && slotWithBuffer.isAfter(new Date());
                }

                return slot.free;
            }) || null
        );
    };

    // If buffer is used, only display deliveryTimes that are valid on buffer
    const slots =
        data?.data.time_slots.filter((slot) => {
            if (!buffer) {
                return true;
            }

            const slotWithBuffer = subsFromDate({
                date: slot.valid_till,
                ...buffer,
            });

            return slotWithBuffer.isAfter(new Date());
        }) || [];

    const deliveryTimes = data ? formatDeliveryTimes(slots) : [];

    return {
        deliveryTimes,
        earliestTimeslot: firstAvailableTimeslot(buffer),
        isLoading,
        isError,
        refetchTimeslots: mutate,
    };
};
